/**
 * Session utilities for managing user session data
 * Adapted from 3in1CMS: bajajfinserv-web/ui.solid/src/lib/util/util.ts
 */

import { decrypt } from "./crypto";

/**
 * Type for session data keys
 */
export type SessionKeys =
  | "mobileNo"
  | "custType"
  | "custId"
  | "email"
  | "requestId"
  | "fullName"
  | "dob"
  | "firstName"
  | "lastName"
  | "isOffers"
  | "newDate"
  | "city"
  | "dealerId"
  | "cndFlag"
  | "dealerName"
  | "reKycFlag"
  | "kycRenewalDate"
  | "pinCode"
  | "indivCorpFlag"
  | "panNo"
  | "easyEmiProduct"
  | "rekycflag"
  | "kycProcess";

/**
 * User session data interface
 */
export interface UserSessionData {
  custType: string;
  custId?: string;
  mobileNo: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  dob?: string;
  newDate: string;
  requestId: string;
  email: string;
  isOffers: string | boolean;
  city: string;
  dealerId: string;
  dealerName: string;
  cndFlag?: boolean;
  reKycFlag?: string;
  kycRenewalDate?: string;
  pinCode?: string;
  indivCorpFlag?: string;
  easyEmiProduct?: string;
  rekycflag?: string;
  kycProcess?: string;
  panNo?: string;
}

/**
 * Get a cookie value by name
 *
 * @param {string} cookieName - The name of the cookie
 * @returns {string} - The cookie value or empty string
 */
export function getCookie(cookieName: string): string {
  if (typeof document === "undefined") return "";

  const name = `${cookieName}=`;
  const cookieData = document.cookie?.split(";");
  if (!cookieData) return "";

  for (let i = 0; i < cookieData.length; i++) {
    let cookie = cookieData[i];
    if (!cookie) continue;

    while (cookie.charAt(0) === " ") {
      cookie = cookie.substring(1);
    }
    if (cookie.indexOf(name) === 0) {
      const cookieValue = cookie.substring(name.length, cookie.length);
      try {
        return decodeURIComponent(cookieValue);
      } catch (error) {
        if (error instanceof URIError) {
          return cookieValue;
        }
      }
    }
  }
  return "";
}

/**
 * Set a cookie with optional expiration time
 *
 * @param {string} cookieName - The name of the cookie
 * @param {number} time - Expiration time in minutes (0 for session cookie)
 * @param {string} value - The cookie value
 */
export const setCookie = (cookieName: string, time: number, value: string): void => {
  if (typeof document === "undefined") return;

  const date = new Date();
  let expires: string;
  if (time > 0) {
    const cookieTime = time;
    date.setTime(date.getTime() + cookieTime * 60 * 1000);
    expires = `expires=${date.toUTCString()}`;
    document.cookie = `${cookieName}=${value}; path=/; domain=.bajajfinserv.in;  ${expires};`;
  } else {
    document.cookie = `${cookieName}=${value}; path=/; domain=.bajajfinserv.in;`;
  }
};

/**
 * Remove a cookie
 *
 * @param {string} cookieName - The name of the cookie to remove
 */
export const removeCookie = (cookieName: string): void => {
  if (typeof document === "undefined") return;

  document.cookie = `${cookieName}=; path=/; domain=.bajajfinserv.in; expires=Thu, 01 Jan 1970 00:00:00 GMT; Max-Age=0;`;
};

/**
 * Get session data from cookies
 * Decrypts sensitive fields like mobile number, email, and DOB
 *
 * @returns {Promise<UserSessionData>} - The user session data
 */
export const getSessionData = async (): Promise<UserSessionData> => {
  const sessionObj: UserSessionData | null = getCookie("userDetails")
    ? (JSON.parse(getCookie("userDetails")) as UserSessionData)
    : null;

  if (sessionObj) {
    const encryptedMobileNo = sessionObj.mobileNo;
    const encryptedDob = sessionObj.dob;
    const encryptedEmail = sessionObj.email;
    const salt = sessionObj.newDate;

    // Decrypt sensitive fields
    const decryptedMobileNo = encryptedMobileNo ? await decrypt(encryptedMobileNo, salt) : "";
    const decryptedDob = encryptedDob ? await decrypt(encryptedDob, salt) : "";
    const decryptedEmail = encryptedEmail ? await decrypt(encryptedEmail, salt) : "";

    return {
      ...sessionObj,
      mobileNo: `${decryptedMobileNo}`,
      dob: `${decryptedDob}`,
      email: `${decryptedEmail}`,
    };
  }

  // Return default session data if no session found
  return {
    custType: "NTB",
    mobileNo: "",
    fullName: "",
    dob: "",
    firstName: "",
    lastName: "",
    newDate: "",
    requestId: "",
    email: "",
    isOffers: "",
    city: "",
    dealerId: "",
    dealerName: "",
    cndFlag: false,
    reKycFlag: "",
    kycRenewalDate: "",
    pinCode: "",
    indivCorpFlag: "",
  };
};

/**
 * Set session data in cookies
 *
 * @param {UserSessionData} userDetails - The user session data to store
 */
export function setSessionDataCookie(userDetails: UserSessionData): void {
  setCookie("userDetails", 0, JSON.stringify(userDetails));
}

/**
 * Get URL parameter value by name
 *
 * @param {string} paramsValue - The parameter name to retrieve
 * @returns {string | null} - The parameter value or null if not found
 */
export const getParams = (paramsValue: string): string | null => {
  if (typeof window === "undefined") return null;

  const currentUrl = window.location.href;
  const url = new URL(currentUrl);
  const params = url.searchParams;
  return params.get(paramsValue);
};

/**
 * Check if running in test environment
 *
 * @returns {boolean} - True if in test environment
 */
export const isTestEnvironment = (): boolean => {
  return (
    (typeof process !== "undefined" && process.env?.NODE_ENV === "test") ||
    (typeof global !== "undefined" && "jest" in global) ||
    (typeof global !== "undefined" && "__vitest__" in global) ||
    (typeof window !== "undefined" &&
      "__JEST__" in (window as unknown as Record<string, unknown>)) ||
    (typeof window !== "undefined" &&
      "__vitest__" in (window as unknown as Record<string, unknown>))
  );
};
